/**
 * Create party modal component for DEX admin/super-admin workflow.
 * Allows admins to create writers/readers and super-admins to create admins/super-admins.
 * Includes optional keypair generation for reader parties.
 */

import { showToast } from './toast.js';
import { showKeygenModal, hideKeygenModal } from './keygen-modal.js';

// Module-level state
let modalState = {
  visible: false,
  roleOptions: [],
  selectedRole: '',
  partyId: '',
  publicKey: '',
  error: null,
  status: 'idle'
};

// Nested modal container for keygen
let keygenCleanup = null;

/**
 * Validate party ID (non-empty).
 * @param {string} partyId - Party ID to validate
 * @returns {Object} { valid: boolean, error?: string }
 */
function validatePartyId(partyId) {
  if (!partyId || !partyId.trim()) {
    return { valid: false, error: 'Party ID is required' };
  }
  return { valid: true };
}

/**
 * Validate public key format (base64, 44 chars for 32-byte key).
 * @param {string} publicKey - Public key to validate
 * @returns {Object} { valid: boolean, error?: string }
 */
function validatePublicKey(publicKey) {
  if (!publicKey || !publicKey.trim()) {
    return { valid: false, error: 'Public key is required for readers' };
  }
  const trimmed = publicKey.trim();
  // Base64 encoded 32-byte key should be 44 characters
  if (trimmed.length !== 44) {
    return { valid: false, error: 'Public key must be 44 characters (base64 encoded 32 bytes)' };
  }
  // Check base64 format
  const base64Regex = /^[A-Za-z0-9+/]+=*$/;
  if (!base64Regex.test(trimmed)) {
    return { valid: false, error: 'Public key must be valid base64 format' };
  }
  return { valid: true };
}

/**
 * Render the create party modal into a container.
 * @param {HTMLElement} container - Container element to render into
 * @param {Object} state - Modal state
 * @param {boolean} [state.visible=false] - Whether modal is visible
 * @param {string[]} [state.roleOptions=[]] - Available roles
 * @param {string} [state.selectedRole=''] - Currently selected role
 * @param {string} [state.partyId=''] - Party ID form value
 * @param {string} [state.publicKey=''] - Public key form value (for readers)
 * @param {string|null} [state.error=null] - Error message
 * @param {string} [state.status='idle'] - Current status: 'idle' | 'creating'
 */
export function renderCreatePartyModal(container, state = {}) {
  const {
    visible = false,
    roleOptions = [],
    selectedRole = '',
    partyId = '',
    publicKey = '',
    error = null,
    status = 'idle'
  } = state;

  if (!visible) {
    container.innerHTML = '';
    return;
  }

  // Determine header based on role options
  const isAdminCreation = roleOptions.includes('admin') || roleOptions.includes('super_admin');
  const headerText = isAdminCreation ? 'Create Admin/Super-Admin' : 'Create Party';

  // Error display
  const errorHtml = error
    ? `<div class="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
        <p class="text-sm text-red-800">${escapeHtml(error)}</p>
      </div>`
    : '';

  // Role options as radio buttons
  const roleOptionsHtml = roleOptions.map((role, index) => {
    const roleLabel = formatRoleLabel(role);
    const isChecked = selectedRole === role || (index === 0 && !selectedRole);
    return `
      <label class="flex items-center">
        <input
          type="radio"
          name="party-role"
          value="${role}"
          ${isChecked ? 'checked' : ''}
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          ${status === 'creating' ? 'disabled' : ''}
        />
        <span class="ml-2 text-sm text-gray-700">${roleLabel}</span>
      </label>
    `;
  }).join('');

  // Effective selected role for conditional UI
  const effectiveRole = selectedRole || roleOptions[0] || '';
  const showPublicKey = effectiveRole === 'reader';

  // Public key section (only for readers)
  const publicKeyHtml = showPublicKey ? `
    <div class="mb-4" id="create-party-public-key-section">
      <label for="create-party-public-key" class="block text-sm font-medium text-gray-700 mb-1">
        Public Key
      </label>
      <textarea
        id="create-party-public-key"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none"
        rows="2"
        placeholder="Base64 encoded public key (44 characters)..."
        ${status === 'creating' ? 'disabled' : ''}
      >${escapeHtml(publicKey)}</textarea>
      <div class="flex justify-between items-center mt-1">
        <p class="text-gray-400 text-xs">Required for readers to receive encrypted payloads</p>
        <button
          type="button"
          id="create-party-generate-keypair-btn"
          class="text-xs text-blue-600 hover:text-blue-800 underline"
          ${status === 'creating' ? 'disabled' : ''}
        >
          Generate new keypair
        </button>
      </div>
    </div>
  ` : '';

  container.innerHTML = `
    <div
      id="create-party-modal-backdrop"
      class="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">${headerText}</h2>
          <button
            id="create-party-close-btn"
            class="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
            ${status === 'creating' ? 'disabled' : ''}
          >
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <form id="create-party-form" class="px-6 py-4">
          ${errorHtml}

          <!-- Party ID -->
          <div class="mb-4">
            <label for="create-party-id" class="block text-sm font-medium text-gray-700 mb-1">
              Party ID
            </label>
            <input
              type="text"
              id="create-party-id"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter party identifier..."
              value="${escapeHtml(partyId)}"
              ${status === 'creating' ? 'disabled' : ''}
              required
            />
            <p class="text-gray-400 text-xs mt-1">Unique identifier for this party</p>
          </div>

          <!-- Role Selection -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <div class="space-y-2">
              ${roleOptionsHtml}
            </div>
          </div>

          ${publicKeyHtml}
        </form>

        <div class="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            id="create-party-cancel-btn"
            class="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            ${status === 'creating' ? 'disabled' : ''}
          >
            Cancel
          </button>
          <button
            id="create-party-submit-btn"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            ${status === 'creating' ? 'disabled' : ''}
          >
            ${status === 'creating' ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>

    <!-- Nested keygen modal container -->
    <div id="create-party-keygen-container"></div>
  `;
}

/**
 * Initialize the create party modal with event handlers.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onClose] - Called when modal is closed
 * @param {Function} [callbacks.onRoleChange] - Called when role changes: (role) => void
 * @param {Function} [callbacks.onSubmit] - Called on form submit: ({ party_id, role, public_key? }) => void
 * @param {Function} [callbacks.onGenerateKeypair] - Called when generate keypair is clicked
 * @returns {Function} Cleanup function
 */
export function initCreatePartyModal(container, callbacks = {}) {
  const { onClose, onRoleChange, onSubmit, onGenerateKeypair } = callbacks;

  const cleanupFunctions = [];

  // Close handlers (no backdrop click - prevents accidental dismissal)
  const closeBtn = container.querySelector('#create-party-close-btn');
  const cancelBtn = container.querySelector('#create-party-cancel-btn');

  function handleClose() {
    // Clean up nested keygen modal if open
    if (keygenCleanup) {
      keygenCleanup();
      keygenCleanup = null;
    }
    if (onClose) {
      onClose();
    }
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', handleClose);
    cleanupFunctions.push(() => closeBtn.removeEventListener('click', handleClose));
  }

  if (cancelBtn) {
    cancelBtn.addEventListener('click', handleClose);
    cleanupFunctions.push(() => cancelBtn.removeEventListener('click', handleClose));
  }

  // Role change handlers
  const roleRadios = container.querySelectorAll('input[name="party-role"]');
  roleRadios.forEach((radio) => {
    function handleRoleChange(e) {
      modalState.selectedRole = e.target.value;
      if (onRoleChange) {
        onRoleChange(e.target.value);
      }
      // Re-render to show/hide public key field
      renderCreatePartyModal(container, modalState);
      initCreatePartyModal(container, callbacks);
    }
    radio.addEventListener('change', handleRoleChange);
    cleanupFunctions.push(() => radio.removeEventListener('change', handleRoleChange));
  });

  // Party ID input - update state on blur
  const partyIdInput = container.querySelector('#create-party-id');
  if (partyIdInput) {
    function handlePartyIdBlur(e) {
      modalState.partyId = e.target.value.trim();
    }
    partyIdInput.addEventListener('blur', handlePartyIdBlur);
    cleanupFunctions.push(() => partyIdInput.removeEventListener('blur', handlePartyIdBlur));
  }

  // Public key input - update state on blur
  const publicKeyInput = container.querySelector('#create-party-public-key');
  if (publicKeyInput) {
    function handlePublicKeyBlur(e) {
      modalState.publicKey = e.target.value.trim();
    }
    publicKeyInput.addEventListener('blur', handlePublicKeyBlur);
    cleanupFunctions.push(() => publicKeyInput.removeEventListener('blur', handlePublicKeyBlur));
  }

  // Generate keypair button
  const generateKeypairBtn = container.querySelector('#create-party-generate-keypair-btn');
  if (generateKeypairBtn) {
    function handleGenerateKeypair() {
      const keygenContainer = container.querySelector('#create-party-keygen-container');
      if (keygenContainer) {
        keygenCleanup = showKeygenModal(keygenContainer, {
          onClose: () => {
            hideKeygenModal(keygenContainer);
            keygenCleanup = null;
          },
          onGenerate: (publicKey) => {
            // Fill in the public key field
            modalState.publicKey = publicKey;
            const pubKeyInput = container.querySelector('#create-party-public-key');
            if (pubKeyInput) {
              pubKeyInput.value = publicKey;
            }
            showToast('Public key filled from generated keypair', 'success');
            // Close keygen modal
            hideKeygenModal(keygenContainer);
            keygenCleanup = null;
          }
        });
      }
      if (onGenerateKeypair) {
        onGenerateKeypair();
      }
    }
    generateKeypairBtn.addEventListener('click', handleGenerateKeypair);
    cleanupFunctions.push(() => generateKeypairBtn.removeEventListener('click', handleGenerateKeypair));
  }

  // Form submit
  const form = container.querySelector('#create-party-form');
  const submitBtn = container.querySelector('#create-party-submit-btn');

  async function handleSubmit(e) {
    if (e) e.preventDefault();

    // Read values directly from DOM (in case blur didn't fire)
    const partyIdEl = container.querySelector('#create-party-id');
    const publicKeyEl = container.querySelector('#create-party-public-key');
    const selectedRadio = container.querySelector('input[name="party-role"]:checked');

    const partyId = partyIdEl ? partyIdEl.value.trim() : modalState.partyId;
    const publicKey = publicKeyEl ? publicKeyEl.value.trim() : modalState.publicKey;
    const role = selectedRadio ? selectedRadio.value : modalState.selectedRole || modalState.roleOptions[0];

    // Validate party ID
    const partyIdValidation = validatePartyId(partyId);
    if (!partyIdValidation.valid) {
      modalState.error = partyIdValidation.error;
      renderCreatePartyModal(container, { ...modalState, partyId, publicKey, selectedRole: role });
      initCreatePartyModal(container, callbacks);
      showToast(partyIdValidation.error, 'error');
      return;
    }

    // Validate public key for readers
    if (role === 'reader') {
      const publicKeyValidation = validatePublicKey(publicKey);
      if (!publicKeyValidation.valid) {
        modalState.error = publicKeyValidation.error;
        renderCreatePartyModal(container, { ...modalState, partyId, publicKey, selectedRole: role });
        initCreatePartyModal(container, callbacks);
        showToast(publicKeyValidation.error, 'error');
        return;
      }
    }

    // Clear error, set creating status
    modalState = {
      ...modalState,
      partyId,
      publicKey,
      selectedRole: role,
      error: null,
      status: 'creating'
    };
    renderCreatePartyModal(container, modalState);
    initCreatePartyModal(container, callbacks);

    // Build submit data
    const submitData = {
      party_id: partyId,
      role
    };
    if (role === 'reader') {
      submitData.public_key = publicKey;
    }

    // Call submit callback
    if (onSubmit) {
      onSubmit(submitData);
    }
  }

  if (form) {
    form.addEventListener('submit', handleSubmit);
    cleanupFunctions.push(() => form.removeEventListener('submit', handleSubmit));
  }

  if (submitBtn) {
    submitBtn.addEventListener('click', handleSubmit);
    cleanupFunctions.push(() => submitBtn.removeEventListener('click', handleSubmit));
  }

  // Keyboard support: Escape to close
  function handleKeydown(e) {
    if (e.key === 'Escape' && modalState.status !== 'creating') {
      handleClose();
    }
  }
  document.addEventListener('keydown', handleKeydown);
  cleanupFunctions.push(() => document.removeEventListener('keydown', handleKeydown));

  // Return combined cleanup
  return function cleanup() {
    cleanupFunctions.forEach((fn) => fn());
    if (keygenCleanup) {
      keygenCleanup();
      keygenCleanup = null;
    }
  };
}

/**
 * Show the create party modal.
 * Resets state and renders the modal in visible state.
 * @param {HTMLElement} container - Container element
 * @param {string[]} roleOptions - Available roles (e.g., ['writer', 'reader'] or ['admin', 'super_admin'])
 * @param {Object} callbacks - Callback functions
 * @returns {Function} Cleanup function
 */
export function showCreatePartyModal(container, roleOptions, callbacks = {}) {
  // Reset state
  modalState = {
    visible: true,
    roleOptions: roleOptions || [],
    selectedRole: roleOptions?.[0] || '',
    partyId: '',
    publicKey: '',
    error: null,
    status: 'idle'
  };

  // Render and initialize
  renderCreatePartyModal(container, modalState);
  return initCreatePartyModal(container, callbacks);
}

/**
 * Hide the create party modal.
 * Clears the container content.
 * @param {HTMLElement} container - Container element
 */
export function hideCreatePartyModal(container) {
  modalState = {
    visible: false,
    roleOptions: [],
    selectedRole: '',
    partyId: '',
    publicKey: '',
    error: null,
    status: 'idle'
  };
  container.innerHTML = '';
  if (keygenCleanup) {
    keygenCleanup();
    keygenCleanup = null;
  }
}

/**
 * Format role for display.
 * @param {string} role - Role identifier
 * @returns {string} Formatted role label
 */
function formatRoleLabel(role) {
  const labels = {
    writer: 'Writer',
    reader: 'Reader',
    admin: 'Admin',
    super_admin: 'Super Admin'
  };
  return labels[role] || role;
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
